import { BusinessBasicsConfig } from '@/types/configuration'
import { Card, Checkbox, Group, Select, Stack, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useEffect } from 'react'
import { ConfigFormHeader } from '@/components/ConfigFormHeader'

/**
 * Format phone number to (XXX) XXX-XXXX format
 */
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '')
  
  // Limit to 10 digits
  const limitedDigits = digits.slice(0, 10)
  
  // Format based on length
  if (limitedDigits.length === 0) return ''
  if (limitedDigits.length <= 3) return `(${limitedDigits}`
  if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`
}

interface BusinessBasicsFormProps {
  data: BusinessBasicsConfig | null
  onSave: (data: BusinessBasicsConfig) => void
  isSaving: boolean
}

export function BusinessBasicsForm({ data, onSave, isSaving }: BusinessBasicsFormProps) {
  const form = useForm<BusinessBasicsConfig>({
    initialValues: {
      storeName: '',
      oemDesignation: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zipCode: '',
      },
      primaryPhone: '',
      secondaryPhone: '',
      email: '',
      website: '',
      timezone: 'America/Chicago',
      suppressDealershipAddressForCustomers: false,
    },
    validate: {
      storeName: (value) => (value.trim() ? null : 'Store name is required'),
      address: {
        line1: (value) => (value.trim() ? null : 'Address line 1 is required'),
        city: (value) => (value.trim() ? null : 'City is required'),
        state: (value) => (value.trim() ? null : 'State is required'),
        zipCode: (value) => (value.trim() ? null : 'ZIP code is required'),
      },
      primaryPhone: (value) => (value.trim() ? null : 'Primary phone is required'),
      email: (value) => {
        if (!value.trim()) return 'Email is required'
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email format'
        return null
      },
      website: (value) => {
        if (!value.trim()) return 'Website is required'
        if (!/^https?:\/\/.+/.test(value)) return 'Website must start with http:// or https://'
        return null
      },
    },
  })

  // Update form when data changes
  useEffect(() => {
    if (data) {
      form.setValues({
        ...data,
        suppressDealershipAddressForCustomers: data.suppressDealershipAddressForCustomers ?? false,
      })
    }
  }, [data])

  const handleSubmit = (values: BusinessBasicsConfig) => {
    onSave(values)
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <ConfigFormHeader onReset={() => form.reset()} isSaving={isSaving} mb="md" />
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Stack gap="md">
          {/* Store Information */}
          <Group grow>
            <TextInput
              label="Store Name"
              placeholder="Enter store name"
              required
              {...form.getInputProps('storeName')}
            />
            <TextInput
              label="OEM Designation (Optional)"
              placeholder="e.g., Ford, Lincoln, Chevrolet"
              {...form.getInputProps('oemDesignation')}
            />
          </Group>

          {/* Address */}
          <Stack gap="xs">
            <TextInput
              label="Address Line 1"
              placeholder="Street address"
              required
              {...form.getInputProps('address.line1')}
            />
            <TextInput
              label="Address Line 2 (Optional)"
              placeholder="Suite, unit, building, floor, etc."
              {...form.getInputProps('address.line2')}
            />
            <Group grow>
              <TextInput
                label="City"
                placeholder="City"
                required
                {...form.getInputProps('address.city')}
              />
              <TextInput
                label="State"
                placeholder="State"
                required
                {...form.getInputProps('address.state')}
              />
              <TextInput
                label="ZIP Code"
                placeholder="ZIP Code"
                required
                {...form.getInputProps('address.zipCode')}
              />
            </Group>
            <Checkbox
              label="Don't include dealership street address in customer messages"
              description="Use when multiple physical locations share one CRM record. Address stays for internal use; emails, SMS, and AI will not show it, and customers asking for directions are connected to your team."
              {...form.getInputProps('suppressDealershipAddressForCustomers', { type: 'checkbox' })}
            />
          </Stack>

          {/* Contact Information */}
          <Group grow>
            <TextInput
              label="Primary Phone"
              placeholder="(555) 123-4567"
              required
              maxLength={14}
              value={form.values.primaryPhone || ''}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value)
                form.setFieldValue('primaryPhone', formatted)
              }}
              onBlur={() => form.validateField('primaryPhone')}
              error={form.errors.primaryPhone}
            />
            <TextInput
              label="Secondary Phone (Optional)"
              placeholder="(555) 123-4568"
              maxLength={14}
              value={form.values.secondaryPhone || ''}
              onChange={(e) => {
                const formatted = formatPhoneNumber(e.target.value)
                form.setFieldValue('secondaryPhone', formatted)
              }}
              onBlur={() => form.validateField('secondaryPhone')}
              error={form.errors.secondaryPhone}
            />
          </Group>

          <Group grow>
            <TextInput
              label="Email"
              type="email"
              placeholder="info@dealership.com"
              required
              {...form.getInputProps('email')}
            />
            <TextInput
              label="Website"
              placeholder="https://www.dealership.com"
              required
              {...form.getInputProps('website')}
            />
          </Group>

          {/* Timezone */}
          <Select
            label="Timezone"
            data={[
              { value: 'America/New_York', label: 'Eastern Time' },
              { value: 'America/Chicago', label: 'Central Time' },
              { value: 'America/Denver', label: 'Mountain Time' },
              { value: 'America/Los_Angeles', label: 'Pacific Time' },
              { value: 'America/Phoenix', label: 'Arizona Time' },
              { value: 'America/Anchorage', label: 'Alaska Time' },
              { value: 'Pacific/Honolulu', label: 'Hawaii Time' },
            ]}
            {...form.getInputProps('timezone')}
          />

          {/* Actions */}
        </Stack>
      </Card>
    </form>
  )
}

